import { useEffect, useState } from "react";
import { Link } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import { applySeo } from "@/lib/seo";
import {
  setJsonLd,
  organizationSchema,
  websiteSchema,
  buildFaqSchema,
} from "@/lib/schema";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  MarketingHero,
  JumpLinks,
  MarketingSection,
  MarketingSectionHeader,
  MarketingCTA,
  type JumpLink,
} from "@/components/marketing";
import {
  Check,
  ArrowRight,
  Shield,
  Key,
  Phone,
  CreditCard,
  Package,
  FileText,
  Users,
  Clock,
  Star,
  Briefcase,
  HelpCircle,
  type LucideIcon,
} from "lucide-react";
import AddonIllustration from "@/components/marketing/AddonIllustration";
import FeeCalculator from "@/components/marketing/FeeCalculator";

const jumpLinks: JumpLink[] = [
  { label: "What you get", href: "#what-you-get" },
  { label: "Plans", href: "#plans" },
  { label: "Add-ons", href: "#add-ons" },
  { label: "For executors", href: "#for-executors" },
  { label: "Triggers", href: "#triggers" },
  { label: "Recovery fees", href: "#recovery-fees" },
  { label: "Next steps", href: "#next-steps" },
  { label: "FAQs", href: "#faqs" },
];

const faqs = [
  {
    question: "Do you hold my private keys?",
    answer:
      "No. Bitzo is entirely non-custodial. We never hold, store, or have access to private keys or seed phrases. Your assets remain under your control at all times. We coordinate verification, documentation and recovery processes only.",
  },
  {
    question: "What does membership include?",
    answer:
      "Membership includes Client Portal access, a live Policy Pack with versioning, trusted contact management with email ownership confirmation, automated review prompts, scheduled reviews (frequency depends on plan), and access to the recovery activation pathway with full audit trails.",
  },
  {
    question: "How do reviews work?",
    answer:
      "You receive automated prompts every 4 to 6 months to confirm details remain current. Scheduled reviews (remote video calls) are included based on your plan, once yearly for Core, twice yearly for Plus, and flexible scheduling for Private Client members.",
  },
  {
    question: "What is the Bitcoin Watchtower and which plans include it?",
    answer:
      "The Watchtower is watch-only monitoring of Bitcoin addresses you choose, run from Bitzo's own node. You get an email the moment coins move, often before the transaction confirms. It uses public addresses only and never touches keys. Continuity Plus includes up to 3 monitored addresses; Private Client includes up to 20. Core Continuity does not include it.",
  },
  {
    question: "What happens if I cancel?",
    answer:
      "You can cancel at any time. Your active membership (reviews, monitoring and updates) stops at the end of the billing period, but your existing Policy Pack and audit trail remain accessible to you and your trusted contacts, so nothing is lost. You can reactivate whenever you like.",
  },
  {
    question: "What if Bitzo is no longer trading?",
    answer:
      "Your Policy Pack is versioned and can be exported. If you hold a Sealed Continuity Pack, it remains independently accessible by your solicitor or executor. Documented workflows and audit trails reduce single-point-of-failure risk.",
  },
  {
    question: "Is this financial or legal advice?",
    answer:
      "No. Bitzo provides coordination and operational support for crypto security and crypto inheritance. We do not provide financial, legal, or tax advice. We recommend consulting appropriate professionals for such matters.",
  },
];

const plans = [
  {
    name: "Core Continuity",
    description: "For individuals with straightforward holdings",
    monthlyPrice: 19,
    yearlyPrice: 190,
    features: [
      "Client Portal access (Policy Pack, Contacts, Documents, Activity)",
      "Create and maintain a live Policy Pack (versioned)",
      "Add trusted contacts with email ownership confirmation",
      "Automated review prompts every 4 to 6 months",
      "One scheduled review per year (remote)",
      "Recovery activation pathway with audit trail",
    ],
    cta: "/setup?plan=essential",
    ctaText: "Get started",
    popular: false,
  },
  {
    name: "Continuity Plus",
    description: "For more frequent check-ins and priority support",
    monthlyPrice: 49,
    yearlyPrice: 490,
    features: [
      "Everything in Core Continuity",
      "Bitcoin Watchtower, instant alerts when coins move (up to 3 addresses)",
      "Two scheduled reviews per year",
      "Priority scheduling for support",
      "Additional document pack versions",
      "Extended contact verification options",
    ],
    cta: "/setup?plan=family",
    ctaText: "Get started",
    popular: true,
  },
  {
    name: "Private Client",
    description: "For complex structures or hands-on guidance",
    monthlyPrice: 149,
    yearlyPrice: 1490,
    features: [
      "Everything in Continuity Plus",
      "Full Bitcoin Watchtower, monitor up to 20 addresses",
      "Named specialist coordinator",
      "Guided setup sessions (remote or in-person)",
      "Executor and solicitor briefing call included",
      "Complex structure support (multisig, multiple wallets, mixed platforms)",
    ],
    cta: "/book",
    ctaText: "Book consultation",
    popular: false,
  },
];

type Addon = {
  icon: LucideIcon;
  name: string;
  code: string;
  description: string;
  price: string;
  included: string | null;
  image?: string;
  illustration?: "onboarding" | "call" | "briefing" | "pack";
};

const addons: Addon[] = [
  {
    icon: Phone,
    name: "Guided Onboarding Call",
    code: "guided_onboarding_call",
    illustration: "onboarding",
    description:
      "A scheduled call with a UK-based specialist to walk you through portal setup, contact verification and Policy Pack creation. Ideal for Core members who want hands-on guidance.",
    price: "£95",
    included: "Included in Plus and Private Client",
  },
  {
    icon: Phone,
    name: "Extra Review Call",
    code: "extra_review_call",
    illustration: "call",
    description:
      "Additional scheduled review beyond your plan allowance. Review your contacts, wallet structure and recovery intent with a specialist.",
    price: "£75 per call",
    included: null,
  },
  {
    icon: Users,
    name: "Executor Briefing Call",
    code: "executor_briefing_call",
    illustration: "briefing",
    description:
      "A dedicated call for your named executor or solicitor to understand Bitzo's recovery process, documentation and their role in coordination.",
    price: "£125",
    included: "Included in Private Client",
  },
  {
    icon: Package,
    name: "Sealed Continuity Pack",
    code: "sealed_continuity_pack",
    illustration: "pack",
    description:
      "A physical sealed pack held by your solicitor or executor. Contains recovery references and instructions, never seed phrases or private keys.",
    price: "£195",
    included: null,
  },
];

const timeline = [
  { step: 1, title: "Checkout", description: "Select your plan and complete payment" },
  { step: 2, title: "Portal invite", description: "Receive secure portal access and begin onboarding" },
  { step: 3, title: "Add contacts", description: "Add trusted contacts and confirm email ownership" },
  { step: 4, title: "Stay current", description: "Automated prompts and reviews keep your plan ready" },
];

function calculateSavings(monthly: number, yearly: number): number {
  const annualFromMonthly = monthly * 12;
  const savings = ((annualFromMonthly - yearly) / annualFromMonthly) * 100;
  return Math.round(savings);
}

export default function Pricing() {
  const [isYearly, setIsYearly] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled") === "1") setShowCanceled(true);
  }, []);

  useEffect(() => {
    applySeo({
      title: "Crypto Inheritance Planning Pricing UK | Plans & Costs | Bitzo",
      description:
        "UK pricing for non-custodial crypto security and crypto inheritance planning. Includes onboarding, verification, documented recovery workflows and audit trails. No seed phrases. No custody.",
      path: "/pricing",
    });
    setJsonLd("jsonld-org", organizationSchema);
    setJsonLd("jsonld-website", websiteSchema);
    setJsonLd(
      "jsonld-faq-pricing",
      buildFaqSchema(faqs.map((f) => ({ question: f.question, answer: f.answer })))
    );
  }, []);

  return (
    <SiteLayout>
      <div className="w-full bg-background min-h-screen">
        {showCanceled && (
          <div className="bg-teal-500/10 border-b border-teal-500/30 px-4 py-3" data-testid="banner-checkout-canceled">
            <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
              <p className="text-sm text-teal-100">
                No payment was taken, and nothing has changed. Your plan is still here whenever
                you&apos;re ready, and you can always{" "}
                <Link href="/book" className="underline">book a quick call</Link> first.
              </p>
              <button
                type="button"
                onClick={() => setShowCanceled(false)}
                className="text-teal-300 hover:text-white text-sm flex-shrink-0"
                aria-label="Dismiss"
                data-testid="button-dismiss-canceled"
              >
                ✕
              </button>
            </div>
          </div>
        )}
        <MarketingHero
          mobileImage="/assets/images/pricing_hero_mobile.webp"
          desktopImage="/assets/images/pricing_hero.webp"
          title="Simple, transparent pricing"
          subtitle="Choose the level of coordination and support that fits your needs. No custody, no access to your assets, just structured readiness."
          testId="heading-pricing"
          priority
        >
          {/* Match Home/Security/Inheritance hero layout: CTA row + JumpLinks */}
          <MarketingCTA>
            <Link href="/setup">
              <Button
                size="lg"
                className="bg-teal-600 hover:bg-teal-500"
                data-testid="button-choose-setup"
              >
                Choose your setup
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link href="/book">
              <Button
                size="lg"
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800"
                data-testid="button-speak-specialist"
              >
                Speak to a specialist
              </Button>
            </Link>
          </MarketingCTA>

          <div className="mt-6">
            <JumpLinks links={jumpLinks} delay={0.4} />
          </div>
        </MarketingHero>

        {/* What You Get */}
        <MarketingSection id="what-you-get" maxWidth="4xl" className="scroll-mt-24">
          <MarketingSectionHeader
            title="What you get"
            subtitle="Every plan includes coordinated crypto continuity planning"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <FileText className="w-6 h-6 text-teal-400 mb-3" />
              <h3 className="text-white font-medium mb-1">Live Policy Pack</h3>
              <p className="text-slate-400 text-sm">
                Versioned documentation of wallet structure, signing rules and recovery intent
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <Users className="w-6 h-6 text-teal-400 mb-3" />
              <h3 className="text-white font-medium mb-1">Trusted Contacts</h3>
              <p className="text-slate-400 text-sm">
                Add family, solicitors and executors with email ownership confirmation
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <Clock className="w-6 h-6 text-teal-400 mb-3" />
              <h3 className="text-white font-medium mb-1">Review Prompts</h3>
              <p className="text-slate-400 text-sm">
                Automated prompts every 4 to 6 months to keep contact and access details current
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <Shield className="w-6 h-6 text-teal-400 mb-3" />
              <h3 className="text-white font-medium mb-1">Audit Trail</h3>
              <p className="text-slate-400 text-sm">
                Visibility of actions, changes, verification events and document versions
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <Key className="w-6 h-6 text-teal-400 mb-3" />
              <h3 className="text-white font-medium mb-1">Recovery Pathway</h3>
              <p className="text-slate-400 text-sm">
                Structured activation process when your family or executor needs it
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <Briefcase className="w-6 h-6 text-teal-400 mb-3" />
              <h3 className="text-white font-medium mb-1">Portal Access</h3>
              <p className="text-slate-400 text-sm">
                Secure client portal for contacts, documents and activity history
              </p>
            </div>
          </div>
        </MarketingSection>

        {/* Why subscription */}
        <MarketingSection id="why-subscription" maxWidth="3xl" background="muted" withBorder className="scroll-mt-24">
          <MarketingSectionHeader
            title="Why ongoing membership?"
            subtitle="Life changes, your crypto readiness should keep pace"
          />
          <div className="space-y-4">
            <p className="text-slate-300 leading-relaxed">
              Crypto inheritance and security planning isn't a one-time document. Your life changes, and so does everything connected to your plan:
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Contact drift:</strong> Phone numbers change, email addresses expire, trusted contacts move or become unreachable.</span>
              </li>
              <li className="flex items-start gap-3">
                <Key className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Wallet drift:</strong> New exchanges, new wallets, new 2FA devices. Your documented structure can fall out of date.</span>
              </li>
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Executor changes:</strong> Solicitors retire, executors move abroad, family circumstances evolve.</span>
              </li>
              <li className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Regular review prompts:</strong> Automated reminders and scheduled reviews ensure your Policy Pack stays current, so it actually works when needed.</span>
              </li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">
              Membership is readiness maintenance. Without ongoing checks, a once-perfect plan can become a recovery failure.
            </p>
          </div>
        </MarketingSection>

        {/* Plans */}
        <MarketingSection id="plans" maxWidth="6xl" className="scroll-mt-24">
          <div className="flex flex-col items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-4 bg-slate-900/50 border border-slate-700/50 rounded-full px-6 py-3">
              <Label
                htmlFor="billing-toggle"
                className={`text-sm font-medium transition-colors ${
                  !isYearly ? "text-white" : "text-slate-400"
                }`}
              >
                Monthly
              </Label>
              <Switch
                id="billing-toggle"
                checked={isYearly}
                onCheckedChange={setIsYearly}
                data-testid="toggle-billing"
              />
              <Label
                htmlFor="billing-toggle"
                className={`text-sm font-medium transition-colors ${
                  isYearly ? "text-white" : "text-slate-400"
                }`}
              >
                Yearly
              </Label>
              {isYearly && (
                <span className="bg-teal-600/20 text-teal-400 text-xs font-medium px-2 py-1 rounded-full">
                  Save up to 17%
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm text-center max-w-2xl">
              Yearly billing is discounted. You can change plan level at renewal.
            </p>
            <p className="text-slate-500 text-xs text-center max-w-2xl" data-testid="text-vat-note">
              All prices are in GBP and include VAT where applicable.
            </p>
          </div>

          {/* Which plan is right for you? */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-white text-center mb-2">Which plan is right for you?</h2>
            <p className="text-slate-400 text-sm text-center mb-8 max-w-2xl mx-auto">
              Three plans, all UK-based, all non-custodial, Bitzo never holds your keys, seed phrases, or funds. Pick the level of support that matches your situation.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="p-5 rounded-xl bg-slate-900/50 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">Core Continuity</h3>
                <p className="text-slate-400 text-sm leading-relaxed">If you hold crypto in one or two wallets and you mainly want a documented recovery path so your family or executor can act if something happens to you.</p>
              </div>
              <div className="p-5 rounded-xl bg-slate-900/80 border border-teal-500 relative">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-teal-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                  <Star className="w-3 h-3" />
                  Most popular
                </span>
                <h3 className="text-white font-semibold mb-2">Continuity Plus</h3>
                <p className="text-slate-400 text-sm leading-relaxed">If your situation changes more often (new wallets, new contacts, life events) and you want more frequent reviews plus priority support.</p>
              </div>
              <div className="p-5 rounded-xl bg-slate-900/50 border border-slate-700/50">
                <h3 className="text-white font-semibold mb-2">Private Client</h3>
                <p className="text-slate-400 text-sm leading-relaxed">If you have complex holdings (multisig, multiple platforms, multi-jurisdiction), need a named specialist, or want hands-on coordination with your IFA, solicitor, accountant, or family office.</p>
              </div>
            </div>
            <p className="text-center text-slate-400 text-sm">
              Not sure?{" "}
              <Link href="/book" className="text-teal-400 hover:underline">
                Book a 20-minute discovery call
              </Link>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan, index) => {
              const savings = calculateSavings(plan.monthlyPrice, plan.yearlyPrice);
              const displayPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
              const period = isYearly ? "/year" : "/month";

              return (
                <div
                  key={plan.name}
                  className={`relative flex flex-col rounded-2xl border p-6 lg:p-8 transition-all ${
                    plan.popular
                      ? "border-teal-500 bg-slate-900/80 shadow-lg shadow-teal-500/10"
                      : "border-slate-700/50 bg-slate-900/50"
                  }`}
                  data-testid={`card-plan-${index}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 bg-teal-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                        <Star className="w-3 h-3" />
                        Most popular
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-white mb-1">{plan.name}</h3>
                    <p className="text-slate-400 text-sm">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">£{displayPrice}</span>
                      <span className="text-slate-400">{period}</span>
                    </div>
                    {isYearly && (
                      <p className="text-teal-400 text-sm mt-1">Save {savings}% vs monthly</p>
                    )}
                  </div>

                  <div className="flex-1 mb-6">
                    <p className="text-sm font-medium text-white mb-4">Included:</p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    asChild
                    className={`w-full ${
                      plan.popular ? "bg-teal-600 hover:bg-teal-500" : "bg-slate-700 hover:bg-slate-600"
                    }`}
                    data-testid={`button-plan-${index}`}
                  >
                    <Link href={plan.cta}>
                      {plan.ctaText}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              );
            })}
          </div>

          {/* What's included in every plan */}
          <div className="mt-10 mb-4">
            <h2 className="text-2xl font-bold text-white mb-6">What's included in every plan</h2>
            <ul className="space-y-3">
              {[
                "Initial setup and architecture review.",
                "Creation of the Policy Pack (your plain-language recovery reference).",
                "Contact verification and ongoing audit trail.",
                "The first recovery rehearsal.",
                "Bitzo never holds keys, seed phrases, or any client asset.",
                "UK-based team. No regulated financial, legal, or tax advice given.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What about recovery execution fees? */}
          <div className="mt-8 p-6 rounded-xl bg-slate-900/50 border border-slate-700/50">
            <h2 className="text-xl font-bold text-white mb-3">What about recovery execution fees?</h2>
            <p className="text-slate-300 text-sm leading-relaxed">
              Your monthly subscription keeps your plan ready. It only costs more if the plan is
              actually used: if a recovery is triggered (for example, by an executor after a death),
              a one-off execution fee applies at that point. We would rather you saw the number now
              than be surprised by it later.
            </p>
            <p className="text-white text-base font-semibold mt-4">
              The execution fee is 10% of the value recovered, never less than £999 and never more than £20,000.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mt-2">
              So on a £150,000 recovery that is £15,000, and on anything from £200,000 upwards it is
              capped at £20,000, however large the estate. It is a one-off, paid from the estate (not out
              of your pocket while you are alive), and many families earmark a whole-of-life policy to cover it.
            </p>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Full worked examples and how it is funded:{" "}
            <a href="#recovery-fees" className="text-teal-400 hover:underline">
              recovery execution fees
            </a>
            .
          </p>
        </MarketingSection>

        {/* Add-ons */}
        <MarketingSection
          id="add-ons"
          maxWidth="6xl"
          background="muted"
          withBorder
          className="scroll-mt-24"
        >
          <MarketingSectionHeader
            title="Add-ons"
            subtitle="Enhance security and resilience without custody"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addons.map((addon, index) => (
              <div
                key={addon.name}
                className="flex flex-col rounded-xl border border-slate-700/50 bg-slate-900/50 p-6"
                data-testid={`card-addon-${addon.code}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-2 rounded-lg bg-teal-600/10">
                    <addon.icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white leading-tight min-h-[2.75rem]">{addon.name}</h3>
                    <p className="text-teal-400 text-sm font-medium mt-1">{addon.price}</p>
                  </div>
                </div>

                {addon.illustration ? (
                  <AddonIllustration kind={addon.illustration} />
                ) : addon.image ? (
                  <img
                    src={addon.image}
                    alt={addon.name}
                    className="w-full h-32 object-contain rounded-lg bg-slate-800/50 mb-4"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : null}

                <p className="text-slate-400 text-sm flex-1">{addon.description}</p>
                
                {addon.included && (
                  <p className="text-teal-400/80 text-xs mt-3 pt-3 border-t border-slate-700/50">
                    {addon.included}
                  </p>
                )}
              </div>
            ))}
          </div>
        </MarketingSection>

        {/* For executors */}
        <MarketingSection id="for-executors" maxWidth="3xl" className="scroll-mt-24">
          <MarketingSectionHeader
            title="For executors and solicitors"
            subtitle="Professional coordination when it matters most"
          />
          <div className="space-y-4">
            <p className="text-slate-300 leading-relaxed">
              When a recovery event is triggered, Bitzo works with executors and solicitors to coordinate the documented process.
              We provide workflows, verification records and guidance, without ever holding keys or authorising transactions.
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Clear, documented recovery procedures</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Briefing calls available for executor and solicitor alignment</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Full audit trail of verification and contact history</span>
              </li>
            </ul>
          </div>
        </MarketingSection>

        {/* Triggers */}
        <MarketingSection id="triggers" maxWidth="3xl" background="muted" withBorder className="scroll-mt-24">
          <MarketingSectionHeader title="Recovery triggers" />
          <div className="space-y-6">
            <p className="text-slate-300 leading-relaxed">
              Recovery is only activated when specific conditions are met. Bitzo coordinates verification, we never initiate transactions and we never hold private keys.
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Death certificate and/or probate documentation</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Verified request from the named executor or solicitor</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Multi-party checks for additional security</span>
              </li>
            </ul>
          </div>
        </MarketingSection>

        {/* Recovery fees */}
        <MarketingSection id="recovery-fees" maxWidth="3xl" className="scroll-mt-24">
          <MarketingSectionHeader title="Recovery execution fees" />
          <div className="space-y-6">
            <p className="text-slate-300 leading-relaxed">
              Recovery fees are only charged if a recovery event is triggered. Bitzo coordinates the process, we never hold keys or authorise transactions.
            </p>

            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Charged only when recovery is activated, never while you are alive and well</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>One flat rate, with no hourly billing and no surprise add-ons on top</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Delivered alongside executors and solicitors</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>
                  Whole-of-life policy support: if you already have a policy, we can record it in your Policy Pack so
                  proceeds can be earmarked to fund fees (subject to executor authority and policy terms).{" "}
                  <a href="#whole-life-policy" className="text-teal-400 hover:underline">
                    Learn more
                  </a>
                </span>
              </li>
            </ul>

            <p className="text-xl font-semibold text-white">
              <strong>10% of recovered portfolio value (minimum £999, maximum £20,000)</strong>
            </p>

            {/* Worked examples so the number is never a surprise */}
            <div className="rounded-xl border border-slate-700/50 bg-slate-900/50 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700/50">
                    <th className="text-left font-medium px-4 py-2">If your family recovers…</th>
                    <th className="text-right font-medium px-4 py-2">The one-off execution fee is…</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  <tr className="border-b border-slate-800/60">
                    <td className="px-4 py-2">£100,000</td>
                    <td className="px-4 py-2 text-right">£10,000</td>
                  </tr>
                  <tr className="border-b border-slate-800/60">
                    <td className="px-4 py-2">£500,000</td>
                    <td className="px-4 py-2 text-right">£20,000 (capped)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">£1,000,000</td>
                    <td className="px-4 py-2 text-right">£20,000 (capped)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <FeeCalculator />

            <p className="text-slate-400 text-sm leading-relaxed">
              The fee is capped at £20,000 however large the estate, so it can never become a
              five-figure surprise that scales with your holdings. It is a one-off charge paid from the
              estate at the time of recovery, not an ongoing cost, and it is comparable to what
              estate-administration and probate specialists charge to handle complex assets. The
              difference is that crypto is unrecoverable if it is handled wrongly, so the coordination
              has to be exact.
            </p>

            <p className="text-slate-500 text-sm">
              A “recovered portfolio value” refers to the portion successfully recovered through the coordinated process. Bitzo does not set asset values for tax purposes.
            </p>
          </div>
        </MarketingSection>

        {/* Whole of life policy explainer */}
        <MarketingSection
          id="whole-life-policy"
          maxWidth="4xl"
          background="muted"
          withBorder
          className="scroll-mt-24"
        >
          <MarketingSectionHeader
            title="Whole-of-life policy funding (optional)"
            subtitle="Estate liquidity can make specialist coordination simple for executors"
          />

          <div className="space-y-6">
            <p className="text-slate-300 leading-relaxed">
              If you already have a whole-of-life policy (or another form of estate liquidity), Bitzo can record the policy details
              and the intended use in your Policy Pack. This helps executors understand what to claim, who to contact, and how fees are funded.
            </p>

            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Record provider reference, ownership notes and the adviser/solicitor point of contact</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Document the claim steps and who should be notified, without exposing sensitive data publicly</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                <span>Reviewed during scheduled reviews and confirmed via 4 to 6 month prompts</span>
              </li>
            </ul>

            <p className="text-slate-500 text-sm">
              Bitzo does not sell, arrange or recommend insurance products and does not provide financial advice. We document what exists and how it is used operationally.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="bg-teal-600 hover:bg-teal-500">
                <Link href="/book">
                  Discuss your setup
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                <Link href="/setup">Choose your setup</Link>
              </Button>
            </div>
          </div>
        </MarketingSection>

        {/* Payment options */}
        <MarketingSection maxWidth="4xl" background="muted" withBorder>
          <MarketingSectionHeader title="Payment options" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
              <CreditCard className="w-5 h-5 text-teal-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">Card, Apple Pay, Google Pay</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
              <Clock className="w-5 h-5 text-teal-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">Monthly or yearly billing</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
              <FileText className="w-5 h-5 text-teal-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">VAT receipt emailed for every payment</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
              <Shield className="w-5 h-5 text-teal-400 flex-shrink-0" />
              <span className="text-slate-300 text-sm">Cancel anytime, no lock-in</span>
            </div>
          </div>
        </MarketingSection>

        {/* Next steps */}
        <MarketingSection id="next-steps" maxWidth="4xl" className="scroll-mt-24">
          <MarketingSectionHeader
            title="Next steps"
            subtitle="From checkout to readiness in four steps"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {timeline.map((step, index) => (
              <div key={step.step} className="relative" data-testid={`timeline-step-${index}`}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-teal-600/20 border-2 border-teal-500 flex items-center justify-center mb-4">
                    <span className="text-teal-400 font-bold">{step.step}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-sm">{step.description}</p>
                </div>
                {index < timeline.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-[calc(50%+24px)] w-[calc(100%-48px)] h-0.5 bg-gradient-to-r from-teal-500/50 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </MarketingSection>

        {/* Resilience reassurance */}
        <MarketingSection maxWidth="3xl" background="muted" withBorder>
          <div className="flex flex-col md:flex-row items-start gap-6 p-6 rounded-xl bg-slate-900/50 border border-slate-700/50">
            <div className="p-3 rounded-lg bg-teal-600/10 flex-shrink-0">
              <HelpCircle className="w-8 h-8 text-teal-400" />
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">What if Bitzo disappears before I die?</h3>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span>Your Policy Pack is versioned and can be exported in a controlled way</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span>Sealed Continuity Packs can be held independently by your solicitor or executor</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-teal-400 mt-1 flex-shrink-0" />
                  <span>Audit trails and workflows reduce single-point-of-failure risk</span>
                </li>
              </ul>
              <p className="text-slate-500 text-sm">
                We design for resilience, your recovery pathway does not depend solely on Bitzo’s continued operation.
              </p>
            </div>
          </div>
        </MarketingSection>

        {/* FAQs */}
        <MarketingSection id="faqs" maxWidth="3xl" className="scroll-mt-24">
          <MarketingSectionHeader title="Frequently asked questions" />
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-slate-900/50 border border-slate-700/50 rounded-lg px-6"
                data-testid={`faq-${i}`}
              >
                <AccordionTrigger className="text-white hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </MarketingSection>

        {/* Final CTA */}
        <MarketingSection maxWidth="3xl">
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold text-white">Ready to secure your crypto legacy?</h2>
            <p className="text-slate-400">
              Choose a plan and start onboarding. If you’d like a quick sanity check, book a call.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-teal-600 hover:bg-teal-500" data-testid="button-final-cta">
                <Link href="/setup">
                  Get started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <Link href="/book" data-testid="button-book-call">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Book a call
                </Link>
              </Button>
            </div>
          </div>
        </MarketingSection>

        {/* Common questions, new FAQ accordion */}
        <MarketingSection maxWidth="3xl">
          <h2 className="text-2xl font-bold text-white mb-8">Common questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              {
                q: "Do you take custody of any crypto?",
                a: "No. Bitzo is non-custodial. We never hold keys, seed phrases, or any client asset. The price covers coordination, documentation, and verification, not custody.",
              },
              {
                q: "Can I change plans?",
                a: "Yes. You can change plan level at renewal, upgrade if your situation grows more complex, or step down if it gets simpler.",
              },
              {
                q: "Is there a discount for paying yearly?",
                a: "Yes, annual billing is discounted. Use the Monthly/Yearly toggle above to see annual prices.",
              },
              {
                q: "What payment methods do you accept?",
                a: "UK card payments via Stripe, on monthly or annual billing.",
              },
              {
                q: "Do you provide regulated financial or tax advice?",
                a: "No. Bitzo provides operational coordination only. Regulated advice should come from your IFA, solicitor, or accountant.",
              },
              {
                q: "What if I need something bespoke (corporate treasury, family office, large multisig setups)?",
                a: null,
              },
              {
                q: "What happens if I cancel?",
                a: "You can cancel at any time. Your active membership, reviews, monitoring and updates, stops at the end of the billing period, but your existing Policy Pack and audit trail remain accessible to you and your trusted contacts, so nothing is lost. You can reactivate whenever you like.",
              },
            ].map((item, i) => (
              <AccordionItem
                key={i}
                value={`common-faq-${i}`}
                className="bg-slate-900/50 border border-slate-700/50 rounded-lg px-6"
                data-testid={`common-faq-${i}`}
              >
                <AccordionTrigger className="text-white hover:no-underline py-4">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4">
                  {i === 5 ? (
                    <>
                      Yes. For family offices and seven-figure holdings we scope a bespoke engagement above Private Client, an annual arrangement with a dedicated specialist, an engagement letter, and firm invoicing.{" "}
                      <Link href="/contact" className="text-teal-400 hover:underline">
                        Contact us
                      </Link>{" "}
                      to discuss.
                    </>
                  ) : (
                    item.a
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </MarketingSection>

        {/* Next step */}
        <MarketingSection maxWidth="3xl">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-white">Next step</h2>
            <p className="text-slate-400">
              Not sure which plan fits your situation?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-teal-600 hover:bg-teal-500" data-testid="button-next-step-book">
                <Link href="/book">
                  Book a 20-minute discovery call
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
            <p className="text-slate-500 text-sm">No pressure, no obligation.</p>
          </div>
        </MarketingSection>

        {/* Bottom disclaimer */}
        <MarketingSection maxWidth="3xl">
          <div className="space-y-6 text-center">
            <p className="text-slate-500 text-sm">
              Payment is by card via Stripe (including Apple Pay and Google Pay where available), on monthly or annual billing. A VAT receipt is emailed for every payment.
            </p>
            <p className="text-slate-400 text-xs border-t border-slate-700/50 pt-6">
              Bitzo coordinates verification and recovery. We do not custody assets or store private keys.
            </p>
          </div>
        </MarketingSection>
      </div>
    </SiteLayout>
  );
}